import { queryProStudio } from '@pypestream/api-services';
import { useEffect, useState } from 'react';

import type { Channel } from '../utils/get-channels-from-pypes';
import { getProjectChannels } from '../utils/get-project-channels';

/**
 * What each solution card on Home says beyond its name: where it runs, what
 * it runs on, how big it is (prototype `renderHome`, `.sol-card`). The
 * solutions list query carries only names, so this is read separately, for
 * the whole project at once — two requests, not one per card — and every
 * fact is optional: a card never waits on it, and one that fails to load
 * just shows the name.
 */
export interface SolutionCardFacts {
  /** Agent groups in the solution's latest version. */
  agents?: number;
  updatedAt?: string;
  /** Channels pointing at this solution, by kind. */
  channels: { pype: number; voice: number };
  /** Environments it has a channel in, Live first. */
  envs: ('live' | 'sandbox')[];
}

export interface CardSolution {
  id: string;
  botSolutionId?: string | null;
}

export interface VersionRow {
  id: string;
  solutionId: string;
  updatedAt?: string | null;
}

// Versions, not solutions: every environment's schema has these fields.
const VERSIONS_QUERY = `query HomeSolutionCards($ids: [String!]!) {
  tenancy_solution_versions(where: { solutionId: { _in: $ids } }) {
    id
    solutionId
    updatedAt
  }
}`;

/** Each solution's most recently updated version: the draft being worked on. */
export const latestVersions = (rows: VersionRow[]) => {
  const latest = new Map<string, VersionRow>();
  rows.forEach((r) => {
    const cur = latest.get(r.solutionId);
    if (!cur || (r.updatedAt ?? '') > (cur.updatedAt ?? '')) {
      latest.set(r.solutionId, r);
    }
  });
  return latest;
};

/** Channels of one kind, counted once however many environments they're in. */
const distinct = (channels: Channel[], type: Channel['type']) =>
  new Set(channels.filter((c) => c.type === type).map((c) => c.name || c.id))
    .size;

// Pro Studio's own copy of each version, where its nodes live, keyed
// "<botSolutionId with - for .>-v<N>". Only a count of group rows comes back.
const AGENT_GROUPS_QUERY = `query HomeSolutionAgentGroups($where: tenancy_solution_versions_ps_bool_exp!) {
  tenancy_solution_versions_ps(where: $where) {
    solutionVersionId
    groups: solution_version_nodes_ps_aggregate(where: { nodeType: { _eq: g } }) {
      aggregate { count }
    }
  }
}`;

/** "Org.Solution" → the prefix its Pro Studio versions carry, "Org-Solution-v". */
export const versionPrefix = (botSolutionId: string) =>
  `${botSolutionId.replace(/\./g, '-')}-v`;

/**
 * Agent groups in each solution's latest (highest-numbered) Pro Studio
 * version, by bot solution id.
 */
export const agentsByBotSolution = (
  botSolutionIds: string[],
  versions: { solutionVersionId: string; groups: number }[]
) => {
  const out = new Map<string, number>();
  botSolutionIds.forEach((botId) => {
    const prefix = versionPrefix(botId);
    let best = -1;
    versions.forEach((v) => {
      if (!v.solutionVersionId.startsWith(prefix)) return;
      const n = Number(v.solutionVersionId.slice(prefix.length));
      if (Number.isInteger(n) && n > best) {
        best = n;
        out.set(botId, v.groups);
      }
    });
  });
  return out;
};

/** Pure: put the three reads together per solution. */
export const summarizeCardFacts = ({
  solutions,
  rows,
  agents,
  channels,
}: {
  solutions: CardSolution[];
  rows: VersionRow[];
  /** Agent groups by bot solution id. Null when that read failed. */
  agents: Map<string, number> | null;
  channels: Channel[];
}): Map<string, SolutionCardFacts> => {
  const latest = latestVersions(rows);

  return new Map(
    solutions.map((s) => {
      const row = latest.get(s.id);
      const mine = s.botSolutionId
        ? channels.filter((c) => c.botId === s.botSolutionId)
        : [];
      const envs = (['live', 'sandbox'] as const).filter((e) =>
        mine.some((c) => c.env === e)
      );
      return [
        s.id,
        {
          agents:
            agents && s.botSolutionId
              ? (agents.get(s.botSolutionId) ?? 0)
              : undefined,
          updatedAt: row?.updatedAt ?? undefined,
          channels: {
            pype: distinct(mine, 'widget'),
            voice: distinct(mine, 'twilio_voice'),
          },
          envs,
        },
      ];
    })
  );
};

const readVersionRows = async (ids: string[]): Promise<VersionRow[]> => {
  const res = await queryProStudio<{
    tenancy_solution_versions?: VersionRow[];
  }>(VERSIONS_QUERY, { ids });
  if (res.error) throw res.error;
  return res.data?.tenancy_solution_versions ?? [];
};

const readAgents = async (botSolutionIds: string[]) => {
  if (!botSolutionIds.length) return new Map<string, number>();
  const res = await queryProStudio<{
    tenancy_solution_versions_ps?: {
      solutionVersionId: string;
      groups?: { aggregate?: { count?: number } };
    }[];
  }>(AGENT_GROUPS_QUERY, {
    where: {
      _or: botSolutionIds.map((id) => ({
        solutionVersionId: { _like: `${versionPrefix(id)}%` },
      })),
    },
  });
  if (res.error) throw res.error;
  return agentsByBotSolution(
    botSolutionIds,
    (res.data?.tenancy_solution_versions_ps ?? []).map((v) => ({
      solutionVersionId: v.solutionVersionId,
      groups: v.groups?.aggregate?.count ?? 0,
    }))
  );
};

export const useSolutionCardFacts = ({
  solutions,
  projectId,
  uniqueCustomerId,
  enabled,
}: {
  solutions: CardSolution[];
  projectId?: string;
  uniqueCustomerId?: string;
  enabled: boolean;
}) => {
  const [facts, setFacts] = useState<Map<string, SolutionCardFacts>>(new Map());
  const key = solutions.map((s) => s.id).join(',');

  useEffect(() => {
    if (!enabled || !solutions.length) return;
    let cancelled = false;
    (async () => {
      const botSolutionIds = solutions
        .map((s) => s.botSolutionId)
        .filter((id): id is string => !!id);
      const [rows, channels, agents] = await Promise.all([
        readVersionRows(solutions.map((s) => s.id)).catch((err) => {
          console.warn('[home] solution details unavailable', err);
          return [] as VersionRow[];
        }),
        projectId && uniqueCustomerId
          ? getProjectChannels({ projectId, uniqueCustomerId }).catch((err) => {
              console.warn('[home] channels unavailable', err);
              return [] as Channel[];
            })
          : Promise.resolve([] as Channel[]),
        readAgents(botSolutionIds).catch((err) => {
          console.warn('[home] agent counts unavailable', err);
          return null;
        }),
      ]);
      if (cancelled) return;
      setFacts(summarizeCardFacts({ solutions, rows, agents, channels }));
    })();
    return () => {
      cancelled = true;
    };
    // `key` stands for `solutions`: a new array each render, the same ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, projectId, uniqueCustomerId]);

  return facts;
};

/** "Updated 2 hours ago", "Updated yesterday", "Updated Sep 12". */
export const updatedLabel = (iso: string | undefined, now = Date.now()) => {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return null;
  const mins = Math.round((now - at) / 60_000);
  if (mins < 1) return 'Updated just now';
  if (mins < 60) return `Updated ${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `Updated ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Updated yesterday';
  if (days < 7) return `Updated ${days} days ago`;
  return `Updated ${new Date(at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(new Date(at).getFullYear() !== new Date(now).getFullYear()
      ? { year: 'numeric' }
      : {}),
  })}`;
};
